import UserGuide from '@/components/help/UserGuide';
import { Input, Select, Textarea } from '@/components/ui/FormControls';
import {refreshSaleConsumers} from '@/lib/refreshSaleConsumers';
import Link from '@/components/common/AccessLink';
import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Plus,
  Search,
  ShieldCheck,
  UserRound,
  X,
  Copy,
  KeyRound,
  Mail,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  OWN_TASK_PERMISSIONS,
  inspectPermissions,
  resolvePermissions,
  roleLabel,
  type LoginRole,
} from '@klothiq/domain';
import { createLoginSchema, accountEmailSchema, accessReasonSchema } from '@klothiq/validation';
import { supabase } from '@/lib/supabase';
import { manageLogin } from '@/lib/user-admin';
import { useAuth } from '@/contexts/AuthContext';
import { useMobileDialogViewport } from '@/hooks/useMobileDialogViewport';
import { PermissionPicker } from '@/components/access/PermissionPicker';
import {
  RoleChangeConfirmation,
  type PendingRoleChange,
} from '@/components/access/RoleChangeConfirmation';
import {
  accessActionClass as actionClass,
  accessInputClass as inputClass,
} from '@/components/access/user-access-styles';

interface LoginAccount {
  id: string;
  full_name: string;
  email: string;
  role: LoginRole;
  status: string;
  version: number;
  must_change_password: boolean;
  staff_id: string | null;
  staff_name: string | null;
  permissions: string[];
}
type Mode = 'create' | 'access' | 'disable' | 'enable' | 'archive' | 'reset' | 'request_email';
const accountStatuses: Record<string, { label: string; className: string }> = {
  active: {
    label: 'Active',
    className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  },
  onboarding: {
    label: 'Password setup',
    className: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  },
  disabled: { label: 'Disabled', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  archived: { label: 'Archived', className: 'bg-muted text-muted-foreground' },
  provisioning: { label: 'Setting up', className: 'bg-sky-500/10 text-sky-700 dark:text-sky-400' },
  provisioning_failed: { label: 'Setup issue', className: 'bg-destructive/10 text-destructive' },
};

export default function UsersAccessPage() {
  const { organization,can } = useAuth();
  const client = useQueryClient();
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<Mode | null>(null);
  const [target, setTarget] = useState<LoginAccount | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'staff'>('staff');
  const [permissions, setPermissions] = useState<string[]>([...OWN_TASK_PERMISSIONS]);
  const [pendingRoleChange, setPendingRoleChange] = useState<PendingRoleChange | null>(null);
  const privilegeSection = useRef<HTMLElement>(null);
  const roleGroup = useRef<HTMLFieldSetElement>(null);
  const [permissionError, setPermissionError] = useState('');
  const [staffId, setStaffId] = useState('');
  const [attested, setAttested] = useState(false);
  const [passwordMode, setPasswordMode] = useState<'generate' | 'manual'>('generate');
  const [temporary, setTemporary] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [requestKey, setRequestKey] = useState('');
  const createRequest = useRef<{ signature: string; key: string } | null>(null);
  const [handoff, setHandoff] = useState<{ email: string; password: string } | null>(null);
  const dialogViewportStyle = useMobileDialogViewport(!!mode);
  const accounts = useQuery({
    queryKey: ['login_accounts', organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_login_accounts');
      if (error) throw error;
      return data as LoginAccount[];
    },
    enabled: !!organization?.id,
  });
  const staff = useQuery({
    queryKey: ['unlinked_staff', organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('staff_members')
        .select('id,name,profile_id')
        .eq('organization_id', organization!.id)
        .eq('is_active', true)
        .is('profile_id', null)
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
    enabled: !!organization?.id,
  });
  const visible = (accounts.data ?? []).filter((person) =>
    `${person.full_name} ${person.email} ${person.staff_name ?? ''}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const open = (nextMode: Mode, person?: LoginAccount) => {
    createRequest.current = null;
    setMode(nextMode);
    setTarget(person ?? null);
    setName(person?.full_name ?? '');
    setEmail(nextMode === 'request_email' ? '' : (person?.email ?? ''));
    setRole(person?.role === 'admin' ? 'admin' : 'staff');
    setPermissions(person?.permissions ?? [...OWN_TASK_PERMISSIONS]);
    setPendingRoleChange(null);
    setPermissionError('');
    setStaffId('');
    setAttested(false);
    setPasswordMode('generate');
    setTemporary('');
    setCurrentPassword('');
    setReason('');
    setError('');
    setRequestKey(crypto.randomUUID());
  };
  const close = () => {
    if (!busy) {
      setMode(null);
      setPendingRoleChange(null);
      setCurrentPassword('');
      setTemporary('');
      setError('');
    }
  };
  const changeRole = (nextRole: 'admin' | 'staff') => {
    if (busy || nextRole === role) return;
    const { issues } = inspectPermissions(permissions, nextRole);
    if (issues.length) {
      setPendingRoleChange({ role: nextRole, removed: issues.map((issue) => issue.key) });
      return;
    }
    setRole(nextRole);
    setPermissionError('');
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !mode || pendingRoleChange) return;
    setError('');
    setPermissionError('');
    try {
      const editsAccess = mode === 'create' || mode === 'access';
      if (editsAccess && inspectPermissions(permissions, role).issues.length) {
        setPermissionError(
          'Review the unavailable privileges before saving. Remove them from this draft or choose a compatible role.',
        );
        privilegeSection.current?.focus();
        return;
      }
      const resolved = editsAccess ? resolvePermissions(permissions, role) : permissions;
      let input: unknown;
      if (mode === 'create')
        input = createLoginSchema.parse({
          full_name: name,
          email,
          role,
          staff_id: staffId || null,
          permissions: resolved,
          password_mode: passwordMode,
          temporary_password: passwordMode === 'manual' ? temporary : undefined,
          email_attested: attested,
        });
      else accessReasonSchema.parse(reason);
      if (mode === 'request_email') accountEmailSchema.parse(email);
      if (!currentPassword) throw new Error('Enter your current password to confirm.');
      let submissionKey = requestKey;
      if (mode === 'create') {
        const details = input as ReturnType<typeof createLoginSchema.parse>;
        // Changed account details start a new intent. Identical retries keep the
        // same key; passwords are never retained in this request fingerprint.
        const signature = JSON.stringify({
          full_name: details.full_name,
          email: details.email,
          role: details.role,
          staff_id: details.staff_id ?? null,
          permissions: [...details.permissions].sort(),
          email_attested: details.email_attested,
        });
        if (createRequest.current?.signature !== signature)
          createRequest.current = { signature, key: crypto.randomUUID() };
        submissionKey = createRequest.current.key;
      }
      setBusy(true);
      const result = await manageLogin({
        action: mode,
        request_key: submissionKey,
        current_password: currentPassword,
        ...(mode === 'create'
          ? { input }
          : {
              profile_id: target?.id,
              version: target?.version,
              full_name: name.trim(),
              role,
              permissions: resolved,
              reason: reason.trim(),
              email: email.trim().toLowerCase(),
            }),
      });
      setCurrentPassword('');
      setTemporary('');
      setMode(null);
      if (result.temporary_password)
        setHandoff({ email: result.email ?? target!.email, password: result.temporary_password });
      else
        toast.success(
          result.already_completed
            ? 'This update was already completed. Reset the password if another handoff is needed.'
            : mode === 'request_email'
              ? 'Email change requested. The person must verify it from My Account.'
              : 'Account updated',
        );
      await client.invalidateQueries({ queryKey: ['login_accounts'] });
      await client.invalidateQueries({ queryKey: ['unlinked_staff'] });
      await refreshSaleConsumers(client);
    } catch (error: any) {
      setError(error.issues?.[0]?.message ?? error.message ?? 'Unable to update account.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl space-y-5 p-4 sm:space-y-6 sm:p-6 lg:p-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide text-primary">
            <ShieldCheck className="h-4 w-4" /> Super Admin
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Users & Access</h1>
          <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-muted-foreground">
            Manage personal logins, privileges and assigned work.
          </p>
        </div>
        <button
          className={`${actionClass} w-full shrink-0 border-primary bg-primary text-primary-foreground hover:bg-primary/90 sm:w-auto`}
          onClick={() => open('create')}
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> Add personal login
        </button>
      </header>
      <div className="flex items-start gap-3 rounded-2xl border border-primary/15 bg-primary/5 p-4 text-sm leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <p>
          Each person gets a separate login and chooses a new password at first sign-in. Task
          notifications arrive inside KLOTHIQ.
        </p>
      </div>
      <label className="relative block">
        <span className="sr-only">Search users</span>
        <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          className={`${inputClass} pl-10`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          helpLabel="Search users"
        />
      </label>
      {accounts.data && (
        <div className="flex items-center justify-between gap-3 px-1 text-sm">
          <h2 className="font-semibold">People</h2>
          <span className="text-xs text-muted-foreground">
            {search
              ? `${visible.length} of ${accounts.data.length}`
              : `${accounts.data.length} logins`}
          </span>
        </div>
      )}
      {accounts.isLoading ? (
        <p
          role="status"
          className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card py-12 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading logins…
        </p>
      ) : accounts.isError ? (
        <div role="alert" className="rounded-2xl border border-destructive/30 p-5">
          <p className="font-medium">The account list could not be loaded.</p>
          <p className="mt-1 text-sm text-muted-foreground">Check your connection and try again.</p>
          <button className={`${actionClass} mt-3`} onClick={() => accounts.refetch()}>
            Retry
          </button>
        </div>
      ) : !visible.length ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <p>{search ? 'No users match your search.' : 'No personal logins yet.'}</p>
          {search && (
            <button type="button" className={`${actionClass} mt-3`} onClick={() => setSearch('')}>
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {visible.map((person) => (
            <article
              key={person.id}
              className="min-w-0 rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  {person.role === 'owner' ? (
                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <UserRound className="h-5 w-5" aria-hidden="true" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="break-words font-semibold leading-snug">{person.full_name}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
                    {person.email}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs">
                      {roleLabel(person.role)}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs ${accountStatuses[person.status]?.className ?? 'bg-muted text-muted-foreground'}`}
                    >
                      {accountStatuses[person.status]?.label ?? person.status.replaceAll('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 rounded-xl bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
                <p>
                  {person.role === 'owner'
                    ? 'Full access · Protected primary account'
                    : `${person.permissions.filter((permission) => !permission.startsWith('lookup.')).length} screen & action privileges`}
                </p>
                {person.staff_name && (
                  <p className="break-words">Linked staff · {person.staff_name}</p>
                )}
                {person.must_change_password && (
                  <p className="flex items-start gap-1.5">
                    <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Must choose a new password at sign-in
                  </p>
                )}
              </div>
              {can('sales_contribution.view_all')&&<Link className={`${actionClass} mt-3`} to={`/team-sales?person=profile:${person.id}`}>View sales activity</Link>}
              {person.role !== 'owner' && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    className={`${actionClass} col-span-2 border-primary/20 bg-primary/5 text-primary hover:bg-primary/10`}
                    onClick={() => open('access', person)}
                  >
                    <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" /> Manage
                    privileges
                  </button>
                  <button className={actionClass} onClick={() => open('request_email', person)}>
                    <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />{' '}
                    <span>Change email</span>
                  </button>
                  <button
                    className={actionClass}
                    disabled={['disabled', 'archived'].includes(person.status)}
                    onClick={() => open('reset', person)}
                  >
                    <KeyRound className="h-4 w-4 shrink-0" aria-hidden="true" />{' '}
                    <span>Reset password</span>
                  </button>
                  <button
                    className={`${actionClass} ${person.status === 'disabled' ? '' : 'col-span-2'} ${['disabled', 'archived'].includes(person.status) ? '' : 'text-destructive'}`}
                    onClick={() =>
                      open(
                        ['disabled', 'archived'].includes(person.status) ? 'enable' : 'disable',
                        person,
                      )
                    }
                  >
                    {['disabled', 'archived'].includes(person.status)
                      ? 'Enable login'
                      : 'Disable login'}
                  </button>
                  {person.status === 'disabled' && (
                    <button className={actionClass} onClick={() => open('archive', person)}>
                      Archive login
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      <Dialog.Root
        open={!!mode}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" data-field-guide-scope />
          <Dialog.Content
            onEscapeKeyDown={(e) => {
              if (busy) e.preventDefault();
            }}
            onInteractOutside={(e) => {
              if (busy) e.preventDefault();
            }}
            style={dialogViewportStyle}
            className="fixed inset-x-0 top-[var(--dialog-mobile-top,0px)] z-50 flex h-[var(--dialog-mobile-height,100dvh)] min-w-0 flex-col overflow-hidden bg-card shadow-2xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[90dvh] sm:w-[calc(100vw-3rem)] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border sm:border-border"
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border bg-card px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] sm:p-5">
              <div className="min-w-0">
                <div className="flex items-start justify-between gap-3 pr-8">
                  <Dialog.Title className="text-lg font-bold">
                    {mode === 'create'
                      ? 'Add a personal login'
                      : mode === 'access'
                        ? 'Manage access'
                        : mode === 'request_email'
                          ? 'Request an email change'
                          : `${mode ? mode.charAt(0).toUpperCase() + mode.slice(1) : ''} login`}
                  </Dialog.Title>
                  <UserGuide compact />
                </div>
                <Dialog.Description className="mt-1 break-words text-sm leading-relaxed text-muted-foreground">
                  {mode === 'create'
                    ? 'Create access for an admin or staff member.'
                    : target?.full_name}
                </Dialog.Description>
              </div>
              <button
                type="button"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-background/40 outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-primary"
                onClick={close}
                disabled={busy}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={save} className="flex min-h-0 flex-1 flex-col" aria-busy={busy}>
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain p-4 sm:p-5">
                <fieldset
                  disabled={busy}
                  className="min-w-0 space-y-5"
                  aria-label="Account details"
                >
                  {mode === 'create' && (
                    <section
                      className="space-y-4 rounded-2xl border border-border bg-background/20 p-4 sm:p-5"
                      aria-label="Person details"
                    >
                      <h3 className="text-sm font-semibold">Person details</h3>
                      <label className="block space-y-2 text-sm font-medium">
                        Full name
                        <Input
                          required
                          maxLength={120}
                          className={inputClass}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          autoComplete="name"
                          helpLabel="Full name"
                        />
                      </label>
                      <label className="block space-y-2 text-sm font-medium">
                        Login email
                        <Input
                          required
                          type="email"
                          maxLength={254}
                          className={inputClass}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="off"
                          helpLabel="Login email"
                        />
                      </label>
                      <label className="block space-y-2 text-sm font-medium">
                        Link existing staff member{' '}
                        <span className="font-normal text-muted-foreground">(optional)</span>
                        <Select
                          className={inputClass}
                          disabled={staff.isLoading || busy}
                          value={staffId}
                          onChange={(e) => setStaffId(e.target.value)}
                          helpLabel="Link existing staff member (optional)"
                        >
                          <option value="">{staff.isLoading ? 'Loading staff…' : 'New person / no staff link'}</option>
                          {staff.data?.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </Select>
                      </label>
                      {staff.isError && (
                        <div
                          className="space-y-2 text-xs leading-relaxed text-muted-foreground"
                          role="status"
                        >
                          <p>
                            Staff links could not be loaded. Retry to link an existing staff member.
                          </p>
                          <button
                            type="button"
                            className={actionClass}
                            onClick={() => staff.refetch()}
                          >
                            Reload staff
                          </button>
                        </div>
                      )}
                      <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-xl bg-muted/30 p-3 text-sm leading-relaxed">
                        <Input
                          required
                          type="checkbox"
                          checked={attested}
                          onChange={(e) => setAttested(e.target.checked)}
                          className="mt-0.5 h-5 w-5 shrink-0 accent-primary"
                          helpLabel="I verified that this email belongs to this person."
                        />
                        <span>I verified that this email belongs to this person.</span>
                      </label>
                    </section>
                  )}
                  {mode === 'access' && (
                    <label className="block space-y-2 text-sm font-medium">
                      Full name
                      <Input
                        required
                        minLength={2}
                        maxLength={120}
                        className={inputClass}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        helpLabel="Full name"
                      />
                    </label>
                  )}
                  {(mode === 'create' || mode === 'access') && (
                    <section
                      ref={privilegeSection}
                      tabIndex={-1}
                      aria-describedby={permissionError ? 'permission-validation-error' : undefined}
                      className="space-y-5 rounded-2xl border border-border bg-background/20 p-4 sm:p-5"
                      aria-label="Role and privileges"
                    >
                      <fieldset ref={roleGroup} tabIndex={-1}>
                        <legend className="mb-2 text-sm font-semibold">Account role</legend>
                        <div className="grid grid-cols-2 gap-2">
                          {(['staff', 'admin'] as const).map((value) => (
                            <label
                              key={value}
                              className={`flex min-h-11 cursor-pointer items-start gap-2.5 rounded-xl border p-3 text-sm transition-colors ${role === value ? 'border-primary/40 bg-primary/5' : 'border-border bg-background/40'}`}
                            >
                              <Input
                                type="radio"
                                name="login_role"
                                value={value}
                                checked={role === value}
                                onChange={() => changeRole(value)}
                                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                                helpLabel="Login role"
                              />
                              <span className="min-w-0">
                                <span className="block font-medium">{roleLabel(value)}</span>
                                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                                  {value === 'staff'
                                    ? 'Assigned work & daily operations'
                                    : 'Business & management access'}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <PermissionPicker
                        role={role}
                        values={permissions}
                        onChange={(next) => {
                          setPermissions(next);
                          setPermissionError('');
                        }}
                        disabled={busy}
                      />
                      {permissionError && (
                        <p
                          id="permission-validation-error"
                          role="alert"
                          className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive"
                        >
                          {permissionError}
                        </p>
                      )}
                      <RoleChangeConfirmation
                        change={pendingRoleChange}
                        onCancel={() => setPendingRoleChange(null)}
                        onReturnFocus={() => roleGroup.current?.focus()}
                        onConfirm={() => {
                          if (!pendingRoleChange || busy) return;
                          const inspected = inspectPermissions(permissions, pendingRoleChange.role);
                          const removed = new Set(inspected.issues.map((issue) => issue.key));
                          setPermissions(permissions.filter((key) => !removed.has(key)));
                          setRole(pendingRoleChange.role);
                          setPermissionError('');
                          setPendingRoleChange(null);
                        }}
                      />
                    </section>
                  )}
                  {mode === 'create' && (
                    <section
                      className="space-y-4 rounded-2xl border border-border bg-background/20 p-4 sm:p-5"
                      aria-label="Login security"
                    >
                      <h3 className="text-sm font-semibold">Login security</h3>
                      <label className="block space-y-2 text-sm font-medium">
                        Temporary password
                        <Select
                          className={inputClass}
                          value={passwordMode}
                          onChange={(e) => {
                            setPasswordMode(e.target.value as 'generate' | 'manual');
                            setTemporary('');
                          }}
                          helpLabel="Temporary password"
                        >
                          <option value="generate">Generate a secure password</option>
                          <option value="manual">Enter a password</option>
                        </Select>
                      </label>
                      {passwordMode === 'manual' && (
                        <label className="block space-y-2 text-sm">
                          Password (12–64 characters)
                          <Input
                            type="password"
                            className={inputClass}
                            autoComplete="new-password"
                            required
                            minLength={12}
                            maxLength={64}
                            value={temporary}
                            onChange={(e) => setTemporary(e.target.value)}
                            helpLabel="Password (12–64 characters)"
                          />
                        </label>
                      )}
                    </section>
                  )}
                  {mode === 'request_email' && (
                    <>
                      <label className="block space-y-2 text-sm font-medium">
                        New login email
                        <Input
                          required
                          type="email"
                          className={inputClass}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          helpLabel="New login email"
                        />
                      </label>
                      <p className="text-sm text-muted-foreground">
                        Their current email keeps working until they approve the request in My
                        Account and confirm the Supabase verification links. The request expires in
                        24 hours.
                      </p>
                    </>
                  )}
                  {mode !== 'create' && (
                    <label className="block space-y-2 text-sm font-medium">
                      Reason
                      <Textarea
                        required
                        minLength={3}
                        maxLength={500}
                        className={inputClass}
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        helpLabel="Reason"
                      />
                    </label>
                  )}
                  {['disable', 'archive'].includes(mode ?? '') && (
                    <p className="rounded-xl bg-amber-500/10 p-3 text-sm text-amber-500">
                      This person will lose portal access. Their sales, task assignments and history
                      remain recorded.
                    </p>
                  )}
                  <label className="block space-y-2 border-t border-border pt-4 text-sm font-medium">
                    Your current password
                    <Input
                      type="password"
                      required
                      autoComplete="current-password"
                      className={inputClass}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      helpLabel="Your current password Enter your own Super Admin password to approve this change."
                    />
                    <span className="block text-xs font-normal leading-relaxed text-muted-foreground">
                      Enter your own Super Admin password to approve this change.
                    </span>
                  </label>
                  {error && (
                    <p
                      role="alert"
                      className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive"
                    >
                      {error}
                    </p>
                  )}
                </fieldset>
              </div>
              <footer className="grid shrink-0 grid-cols-[auto_1fr] gap-3 border-t border-border bg-card px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex sm:justify-end sm:p-4">
                <button type="button" className={actionClass} disabled={busy} onClick={close}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className={`${actionClass} border-primary bg-primary px-5 font-semibold text-primary-foreground hover:bg-primary/90`}
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {busy
                    ? 'Saving…'
                    : mode === 'create'
                      ? 'Create login'
                      : mode === 'access'
                        ? 'Save privileges'
                        : 'Confirm update'}
                </button>
              </footer>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root
        open={!!handoff}
        onOpenChange={(open) => {
          if (!open) setHandoff(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" data-field-guide-scope />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border bg-card p-5 shadow-2xl sm:p-6">
            <ShieldCheck className="mb-3 h-8 w-8 text-primary" />
            <div className="flex items-start justify-between gap-3 pr-8">
              <Dialog.Title className="text-lg font-bold">Login is ready to share</Dialog.Title>
              <UserGuide compact />
            </div>
            <Dialog.Description className="mt-2 text-sm text-muted-foreground">
              Copy these details now and share them privately. The password is shown once. This
              person must choose a new password within 72 hours.
            </Dialog.Description>
            <p className="mt-5 break-all text-sm font-semibold">{handoff?.email}</p>
            <label className="mt-3 block space-y-2 text-sm">
              Temporary password
              <Input
                readOnly
                className={`${inputClass} font-mono`}
                value={handoff?.password ?? ''}
                onFocus={(e) => e.target.select()}
                helpLabel="Temporary password"
              />
            </label>
            <button
              className={`${actionClass} mt-4 w-full`}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(
                    `Email: ${handoff!.email}\nTemporary password: ${handoff!.password}`,
                  );
                  toast.success('Login details copied');
                } catch {
                  toast.error('Clipboard unavailable. Select and copy the details manually.');
                }
              }}
            >
              <Copy className="mr-2 inline h-4 w-4" />
              Copy login details
            </button>
            <Dialog.Close className="mt-3 min-h-11 w-full rounded-xl bg-primary text-sm font-semibold text-primary-foreground">
              Done
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
